if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Derisunum: previously this bailed out entirely when no thumbnail rail existed yet
        // (e.g. the default variant only has a single photo), which meant the viewer never got
        // its slideChanged listener and thumbnail clicks were never wired up even after a
        // thumbnail rail was inserted later by a variant change. Always wire the viewer, and
        // wire thumbnails through a re-runnable method instead.
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnailClicks();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Derisunum: (re)binds click handlers on every thumbnail button currently in the DOM.
      // Safe to call repeatedly — already-bound buttons are skipped via a data flag. Call this
      // after the thumbnail rail is freshly inserted or its items are reconciled (see
      // product-info.js updateMedia), since those buttons were parsed from a fetched HTML
      // fragment and never passed through this constructor.
      bindThumbnailClicks() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button || button.dataset.clickBound) return;
          button.dataset.clickBound = 'true';
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      // Derisunum: called by product-info.js after inserting or removing the whole thumbnail
      // rail wrapper (which only exists in the DOM when the current variant has more than one
      // matching photo), so our cached reference and click bindings stay correct.
      refreshThumbnailElements() {
        this.elements.thumbnails = this.querySelector('[id^="GalleryThumbnails"]');
        this.bindThumbnailClicks();
      }

      onSlideChanged(event) {
        // A variant-driven update (setActiveMedia) just pinned a thumbnail. The main viewer's
        // slider recalculates its pages when the DOM is reordered (prepend) and can dispatch a
        // stale slideChanged event afterwards; because this handler is debounced, that stale
        // event can arrive after the authoritative update and silently revert the highlighted
        // thumbnail. Re-assert the pinned thumbnail instead of trusting it during that window.
        if (this.pinnedThumbnail) {
          this.setActiveThumbnail(this.pinnedThumbnail);
          return;
        }
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          if (this.elements.thumbnails && this.elements.thumbnails.slider) this.elements.thumbnails.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          // Derisunum: instant jump instead of a smooth animated scroll, so variant switches
          // read as a clean cut rather than a slide-like motion.
          window.scrollTo({ top: top });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);

        // Pin this choice briefly so a stale, debounced slideChanged event (see onSlideChanged)
        // can't override it before it fires.
        this.pinnedThumbnail = activeThumbnail;
        clearTimeout(this._pinnedThumbnailTimeout);
        this._pinnedThumbnailTimeout = window.setTimeout(() => {
          this.pinnedThumbnail = null;
        }, 600);

        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
